/**
 * LICITARS 3.0 - Premium Login with Navy/Silver Theme
 * Features: Glassmorphism, smooth animations, responsive design
 */

import React, { useState, useCallback, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import LicitarsLogo from "@/components/LicitarsLogo";
import { Loader2, Lock, User, AlertCircle } from "lucide-react";
import { cn } from "@/lib/utils";

const LoginV3: React.FC = () => {
  const { login, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // Clear tokens when login page is mounted
  useEffect(() => {
    localStorage.removeItem("access_token");
    localStorage.removeItem("refresh_token");
  }, []);

  // Capture return URL from state or localStorage
  const from =
    location.state?.from?.pathname ||
    localStorage.getItem("returnUrl") ||
    "/dashboard";

  // Note: Redirect logic removed - ProtectedRoute handles authenticated user redirects
  // Removing this prevents infinite loop between login and dashboard

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      await login(username, password);
      // Clear return URL after successful login
      localStorage.removeItem("returnUrl");
      // Redirect to previous page or dashboard
      navigate(from, { replace: true });
    } catch (err: any) {
      setError(err?.message || "Credenciais inválidas. Certifique-se de que seu usuário e senha estão corretos.");
    } finally {
      setLoading(false);
    }
  };

  const handleUsernameChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setUsername(e.target.value);
      if (error) setError(""); // Clear error on input change
    },
    [error]
  );

  const handlePasswordChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setPassword(e.target.value);
      if (error) setError(""); // Clear error on input change
    },
    [error]
  );

  // Don't render if already authenticated
  if (isAuthenticated) {
    return null;
  }

  return (
    <div className="min-h-screen w-full bg-navy-800 flex items-center justify-center p-4 relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-navy-900 via-navy-800 to-navy-700 opacity-50"></div>
      <div className="absolute top-0 left-0 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl animate-pulse-glow"></div>
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl animate-pulse-glow" style={{ animationDelay: "1s" }}></div>
      {/* Login Card */}
      <Card className="w-full max-w-md glass-card border-navy-500 relative z-10 animate-fade-in">
        <CardHeader className="space-y-6 pb-8">
          <div className="flex justify-center">
            <LicitarsLogo size="large" />
          </div>
          {/* Title */}
          <div className="text-center space-y-2">
            <CardTitle className="text-3xl font-bold text-white">
              Bem-vindo
            </CardTitle>
            <CardDescription className="text-platinum-400 text-base">
              Plataforma de IA para Gestão de Licitações
            </CardDescription>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Error Alert */}
          {error && (
            <Alert variant="destructive" className="glass-card border-red-500/50 bg-red-500/10 animate-fade-in">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription className="text-red-300">
                {error}
              </AlertDescription>
            </Alert>
          )}
          {/* Login Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Username Field */}
            <div className="space-y-2">
              <Label htmlFor="username" className="text-platinum-300 text-sm font-medium">
                Usuário
              </Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-platinum-400" />
                <Input
                  id="username"
                  type="text"
                  placeholder="Digite seu usuário"
                  value={username}
                  onChange={handleUsernameChange}
                  disabled={loading}
                  required
                  className={cn(
                    "pl-11 glass-card border-navy-500 bg-navy-700/50 text-white placeholder:text-platinum-500",
                    "focus:border-steel-500 focus:ring-2 focus:ring-steel-500/20",
                    "disabled:opacity-50 disabled:cursor-not-allowed",
                    "transition-all duration-200"
                  )}
                  autoComplete="username"
                  autoFocus
                />
              </div>
            </div>
            {/* Password Field */}
            <div className="space-y-2">
              <Label htmlFor="password" className="text-platinum-300 text-sm font-medium">
                Senha
              </Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-platinum-400" />
                <Input
                  id="password"
                  type="password"
                  placeholder="Digite sua senha"
                  value={password}
                  onChange={handlePasswordChange}
                  disabled={loading}
                  required
                  className={cn(
                    "pl-11 glass-card border-navy-500 bg-navy-700/50 text-white placeholder:text-platinum-500",
                    "focus:border-steel-500 focus:ring-2 focus:ring-steel-500/20",
                    "disabled:opacity-50 disabled:cursor-not-allowed",
                    "transition-all duration-200"
                  )}
                  autoComplete="current-password"
                />
              </div>
            </div>
            {/* Submit Button */}
            <Button
              type="submit"
              disabled={loading}
              className={cn(
                "w-full gradient-navy-blue text-white font-semibold py-3 text-base",
                "shadow-blue-glow hover:shadow-lg",
                "disabled:opacity-50 disabled:cursor-not-allowed",
                "transition-all duration-200 transform hover:scale-[1.02] active:scale-[0.98]"
              )}
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                  Entrando...
                </>
              ) : (
                "Entrar"
              )}
            </Button>
          </form>
          {/* Additional Info */}
          <div className="pt-4 border-t border-navy-500">
            <p className="text-center text-xs text-platinum-500">
              Ao entrar, você concorda com nossos{" "}
              <a href="#" className="text-steel-400 hover:text-steel-300 underline">
                Termos de Uso
              </a>{" "}
              e{" "}
              <a href="#" className="text-steel-400 hover:text-steel-300 underline">
                Política de Privacidade
              </a>
            </p>
          </div>
        </CardContent>
      </Card>
      {/* Footer */}
      <div className="absolute bottom-4 left-0 right-0 text-center z-10">
        <p className="text-xs text-platinum-500">
          LicitarsAI © {new Date().getFullYear()} - Todos os direitos reservados
        </p>
      </div>
    </div>
  );
};

export default LoginV3;
